import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./css/LoginFormModal.css"; // Reuse existing modal styles
import "../verification/css/Consent.css"; // Import consent styles for consistency
import UserAPI from "../services/UserAPI";
import { validatePassword, checkPasswordMatch } from "../utils/PasswordUtil";
import PasswordStrengthMeter from "../utils/PasswordStrengthMeter";

function SetPasswordPage() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [setupToken, setSetupToken] = useState("");
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  // Handle confirm password changes
  const handleConfirmPasswordChange = (value) => {
    setConfirmPassword(value);
    const matchResult = checkPasswordMatch(newPassword, value);
    setPasswordMismatch(matchResult.hasError);
    setPasswordsMatch(matchResult.isComplete);
  };
  useEffect(() => {
    // Get password setup token from localStorage (set after email verification)
    const token = localStorage.getItem("passwordSetupToken");
    if (token) {
      setSetupToken(token);
    } else {
      // If no token found, show error
      setErrorMessage(
        "No password setup token found. Please verify your email first to get a setup link.",
      );
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    // Validate inputs
    if (!newPassword.trim()) {
      setErrorMessage("Please enter a password.");
      return;
    }

    // Validate password strength using NIST-compliant PasswordUtil
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setErrorMessage(passwordValidation.message);
      return;
    }

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    if (!setupToken) {
      setErrorMessage(
        "Password setup token not found. Please verify your email first.",
      );
      return;
    }

    try {
      setIsLoading(true);

      // Call API to set password for new user using secure token
      await UserAPI.setNewUserPassword(setupToken, newPassword);

      setSuccessMessage(
        "Password set successfully! You can now log in to your account.",
      );

      // Clear verification data from localStorage
      localStorage.removeItem("passwordSetupToken");
      localStorage.removeItem("verifiedUserUuid");
      localStorage.removeItem("userVerified");

      // Redirect to app page after success
      setTimeout(() => {
        navigate("/app");
      }, 3000);
    } catch (error) {
      console.error("Error setting password:", error);

      // Handle specific error messages from the API
      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("Failed to set password. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoHome = () => {
    navigate("/");
  };

  return (
    <div className="verify-consent-container">
      <div className="verify-consent-card">
        {" "}
        <h1>Set Your Password</h1>
        <p>
          Welcome! Please set up a password for your account to complete the
          setup process.
        </p>
        {!setupToken ? (
          // Show error state when no setup token found
          <div className="verify-message error-message">
            <div className="error-icon">⚠</div>
            <p>{errorMessage}</p>
            <div className="form-buttons" style={{ marginTop: "20px" }}>
              <button
                className="action-button"
                onClick={handleGoHome}
                style={{ backgroundColor: "#4caf50" }}
              >
                Go to Home
              </button>
            </div>
          </div>
        ) : (
          // Show password setup form when user is verified
          <form onSubmit={handleSubmit} className="set-password-form">
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showNewPassword ? "text" : "password"}
                  id="newPassword"
                  name="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  className={errorMessage ? "error" : ""}
                  style={{ paddingRight: "40px" }}
                  minLength={12}
                  maxLength={30}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "5px",
                    fontSize: "16px",
                    color: "#666",
                  }}
                  aria-label={
                    showNewPassword ? "Hide password" : "Show password"
                  }
                >
                  {showNewPassword ? "👁️‍🗨️" : "👁️"}
                </button>
              </div>
              <PasswordStrengthMeter password={newPassword} />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleConfirmPasswordChange(value);
                  }}
                  placeholder="Confirm your password"
                  disabled={isLoading}
                  style={{
                    borderColor: passwordMismatch ? "lightcoral" : "#ddd",
                    paddingRight: "40px",
                  }}
                  minLength={12}
                  maxLength={30}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "5px",
                    fontSize: "16px",
                    color: "#666",
                  }}
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                >
                  {showConfirmPassword ? "👁️‍🗨️" : "👁️"}
                </button>
              </div>
              {passwordMismatch && confirmPassword && (
                <small
                  style={{
                    color: "lightcoral",
                    display: "block",
                    marginTop: "4px",
                  }}
                >
                  Passwords do not match
                </small>
              )}
              {passwordsMatch && (
                <small
                  style={{
                    color: "#28a745",
                    display: "block",
                    marginTop: "4px",
                    fontWeight: "600",
                  }}
                >
                  ✓ Passwords match
                </small>
              )}
            </div>

            <div className="password-requirements">
              <p>
                <strong>Password Requirements:</strong>
              </p>
              <ul>
                <li>
                  Minimum 12 characters (15+ recommended for better security)
                </li>
                <li>
                  Use a memorable passphrase instead of complex characters
                </li>
                <li>
                  Avoid common words, sequential patterns, or repeated
                  characters
                </li>
                <li>Example: "coffee-morning-sunshine-blue"</li>
              </ul>
              <p
                style={{
                  fontSize: "0.85rem",
                  marginTop: "0.5rem",
                  fontStyle: "italic",
                }}
              >
                Strong passphrases are easier to remember and more secure than
                complex short passwords!
              </p>
            </div>

            {errorMessage && (
              <div className="verify-message error-message">
                <p>{errorMessage}</p>
              </div>
            )}

            {successMessage && (
              <div className="verify-message success-message">
                <div className="checkmark-icon">✓</div>
                <p>{successMessage}</p>
                <p>Redirecting to app...</p>
              </div>
            )}

            {!successMessage && (
              <div className="form-buttons">
                <button
                  type="submit"
                  className="action-button"
                  disabled={isLoading}
                  style={{ backgroundColor: "#4caf50" }}
                >
                  {isLoading ? "Setting Password..." : "Set Password"}
                </button>

                <button
                  type="button"
                  className="action-button"
                  onClick={handleGoHome}
                  style={{ marginLeft: "10px", backgroundColor: "#555" }}
                  disabled={isLoading}
                >
                  Cancel
                </button>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

export default SetPasswordPage;
